import type { ComboboxData, ComboboxItem, ComboboxItemGroup } from '@mantine/core';
import { securityDisplayName, spansIssuers } from './scopedBinding.utils.ts';
import type { SecurityOptionRes } from './BindPanel.types.ts';

/**
 * The "which security?" picker's options: grouped under each issuer, in programme order, once
 * the securities span more than one entity, and a flat list otherwise.
 *
 * The label carries the issuer as well as the group, because a closed select shows only the
 * label and two entities can each issue an "Ordinary Shares".
 */
export const securityPickerData = (
	securities: readonly SecurityOptionRes[],
	notHeldSuffix: string
): ComboboxData => {
	const showIssuer = spansIssuers(securities);

	const optionFor = (security: SecurityOptionRes): ComboboxItem => {
		const label = `${securityDisplayName(security, showIssuer)} · ${security.type} · ${security.currency}`;
		// An unheld security stays selectable, dimmed by the suffix rather than removed: an
		// author may be binding a document for a cohort that will hold it later. Only an
		// explicit `false` earns the suffix — `null` is unknown, not unheld.
		const knownUnavailable = security.isHeld === false && security.hasOpenAllocation === false;
		return {
			value: security.id,
			label: knownUnavailable ? `${label} — ${notHeldSuffix}` : label,
		};
	};

	if (!showIssuer) {
		return securities.map(optionFor);
	}

	// Grouped by issuer NAME, which Mantine keys a group on: two entities sharing a name would
	// otherwise collide, and an author could not tell them apart anyway. A nameless issuer's
	// securities stay ungrouped rather than sit under an empty heading.
	const data: (ComboboxItem | ComboboxItemGroup<ComboboxItem>)[] = [];
	const groups = new Map<string, ComboboxItemGroup<ComboboxItem>>();
	for (const security of securities) {
		if (security.issuerName === '') {
			data.push(optionFor(security));
			continue;
		}
		const existing = groups.get(security.issuerName);
		if (existing) {
			existing.items.push(optionFor(security));
			continue;
		}
		const group = { group: security.issuerName, items: [optionFor(security)] };
		groups.set(security.issuerName, group);
		data.push(group);
	}
	return data;
};
